import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { DeallocateButton } from 'src/components/Buttons/DeallocateButton';
import { RejectButton } from 'src/components/Buttons/RejectButton';
import { ReloadButton } from 'src/components/Buttons/ReloadButton';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { ConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { Stack } from 'src/core/ui';
import { SALES_GROUP_QUERY_KEY } from 'src/hooks/api/useGetSalesGroup';
import { useSalesGroupBulkActions } from 'src/hooks/api/useSalesGroupBulkActions';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserCanUpdateSale } from 'src/hooks/useUserHasSalePermissions';
import { ModalFooter } from 'src/modals/Modal/Modal.styled';
import * as saleDetailsStyles from 'src/modals/SaleDetails/SaleDetails.css';
import { ContentId } from 'src/utils/constants/contentId';
import { SALE_CANCELLATION_REASON_CID } from 'src/utils/constants/contentIdMaps';
import {
  Permission,
  Sale,
  SaleActionType,
  SaleCancellationReason,
} from 'src/WebApiController';

/**
 * Bulk Reject / Cancel(Deallocate) footer for the Sales Group modal (POS-8632).
 * Reuses the exact single-sale controls (RejectButton / DeallocateButton +
 * ConfirmDialog + PosEnumSelect cancellation-reason picker + admin-hold Checkbox)
 * and acts as a bulk over the group's eligible member sales via useSalesGroupBulkActions,
 * which loops the per-sale SaleClient endpoints. Mirrors SaleDetailsFooter's layout:
 * <ModalFooter> wrapping the right-aligned styles.actionsContainer row.
 */
export function SalesGroupModalFooter({
  salesGroupId,
  sales,
}: {
  salesGroupId: string;
  sales: Sale[];
}) {
  const { loginContext } = useAppContext();
  const queryClient = useQueryClient();
  const { rejectSales, deallocateSales } =
    useSalesGroupBulkActions(salesGroupId);

  // POS-8632: refetch the group payload so the modal reflects the latest state
  // after out-of-band changes (payments, per-sale edits made elsewhere, etc.).
  const refreshGroup = () =>
    queryClient.invalidateQueries({
      queryKey: [SALES_GROUP_QUERY_KEY, salesGroupId],
    });

  // Gate writes on a representative member sale (mirrors the modal's existing
  // sections). Must be called unconditionally.
  const canUpdate = useUserCanUpdateSale(sales?.[0], false /* isBulkEdit */);

  const hasAdminPermission = useUserHasAnyOfPermissions(
    Permission.SellerAccount_Admin,
    Permission.Inventory_ManageAdminHolds
  );

  const rejectEligible = useMemo(
    () =>
      sales.filter(
        (s) =>
          s.cancellationReason == null &&
          s.actions?.includes(SaleActionType.Reject)
      ),
    [sales]
  );

  const deallocateEligible = useMemo(
    () =>
      sales.filter(
        (s) =>
          s.cancellationReason == null &&
          (s.actions?.includes(SaleActionType.Deallocate) ||
            s.actions?.includes(SaleActionType.RejectAllocation))
      ),
    [sales]
  );

  // Two independent confirm dialogs, one per action, each spanning the whole group.
  const {
    launchDialog: launchRejectDialog,
    closeDialog: closeRejectDialog,
    dialogProps: rejectDialogProps,
  } = useBasicDialog();
  const {
    launchDialog: launchDeallocateDialog,
    closeDialog: closeDeallocateDialog,
    dialogProps: deallocateDialogProps,
  } = useBasicDialog();

  const [cancellationReason, setCancellationReason] =
    useState<SaleCancellationReason | null>(null);
  const [shouldAdminHold, setShouldAdminHold] = useState<boolean>(
    !!loginContext?.user?.activeAccount?.sellerAccountSettings
      ?.adminHoldIndefinitelyForCancelledSales
  );

  const showReject = canUpdate && rejectEligible.length > 0;
  const showDeallocate = canUpdate && deallocateEligible.length > 0;

  return (
    <ModalFooter>
      <div className={saleDetailsStyles.actionsContainer}>
        <ReloadButton
          variant="outline"
          textContentId={ContentId.Refresh}
          titleContentId={ContentId.Refresh}
          disabled={rejectSales.isPending || deallocateSales.isPending}
          onClick={refreshGroup}
        />
        {showReject && (
          <div>
            <RejectButton
              variant="outline"
              disabled={rejectSales.isPending}
              textContentId={ContentId.RejectSale}
              titleContentId={ContentId.RejectSale}
              onClick={() => {
                setCancellationReason(null);
                launchRejectDialog();
              }}
            />
            <ConfirmDialog
              {...rejectDialogProps}
              disabled={rejectSales.isPending}
              isLoading={rejectSales.isPending}
              headerText={<Content id={ContentId.RejectSale} />}
              onOkay={() => {
                rejectSales.mutate({
                  saleIds: rejectEligible.map((s) => s.id),
                  cancellationReason,
                });
                closeRejectDialog();
              }}
              bodyText={
                <Stack direction="column" gap="l">
                  <Stack
                    direction="column"
                    gap="s"
                    className={saleDetailsStyles.cancellationReasonWrapper}
                  >
                    <Content id={ContentId.CancellationReason} />
                    <PosEnumSelect
                      className={saleDetailsStyles.cancellationReasonSelector}
                      valueOptionsContent={SALE_CANCELLATION_REASON_CID}
                      value={cancellationReason}
                      onChange={setCancellationReason}
                    />
                  </Stack>
                  <Content id={ContentId.RejectSaleWarning} />
                </Stack>
              }
            />
          </div>
        )}
        {showDeallocate && (
          <div>
            {/*
              POS-8632: We deliberately do NOT replicate LaunchDeallocateSale's
              Offline+Fulfilled status-flip pre-step here. We rely on actions-based
              eligibility + Promise.allSettled to surface any members that fail.
            */}
            <DeallocateButton
              variant="outline"
              disabled={deallocateSales.isPending}
              textContentId={ContentId.DeallocateSale}
              titleContentId={ContentId.DeallocateSale}
              onClick={() => launchDeallocateDialog()}
            />
            <ConfirmDialog
              {...deallocateDialogProps}
              disabled={deallocateSales.isPending}
              isLoading={deallocateSales.isPending}
              headerText={<Content id={ContentId.DeallocateSale} />}
              onOkay={() => {
                deallocateSales.mutate({
                  saleIds: deallocateEligible.map((s) => s.id),
                  adminHold: shouldAdminHold,
                });
                closeDeallocateDialog();
              }}
              bodyText={
                <Stack direction="column" gap="l">
                  <Content id={ContentId.DeallocateSaleWarning} />
                  {hasAdminPermission && (
                    <Checkbox
                      labelPosition="right"
                      checked={shouldAdminHold}
                      onChange={() => setShouldAdminHold((p) => !p)}
                      label={<Content id={ContentId.PutSaleListingOnHold} />}
                    />
                  )}
                </Stack>
              }
            />
          </div>
        )}
      </div>
    </ModalFooter>
  );
}
